import React from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Profile } from "@/types/supabase";

type HeroProps = {
  className?: string;
  profile: Profile;
};

const Hero: React.FC<HeroProps> = ({ className, profile }) => {
  return (
    <div className={cn("flex flex-col items-start", className)}>
      <div className="inline-flex items-center px-3 py-1.5 glass-morphism rounded-full mb-3 animate-fade-in">
        <span className="h-2 w-2 rounded-full bg-teal mr-2"></span>
        <span className="text-sm font-medium text-gray-300">
          Welcome to my universe
        </span>
      </div>

      <h1
        className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 animate-slide-up"
        style={{ animationDelay: "0.2s" }}
      >
        Hello,
        <br />
        I'm
        <span className="text-teal ml-2 shine-text">
          {profile?.name || "Shishir Regmi"}
        </span>
      </h1>

      <div
        className="glass-morphism px-6 py-3 rounded-full text-gray-300 mb-5 animate-slide-up text-sm sm:text-base"
        style={{ animationDelay: "0.3s" }}
      >
        <span className="shine-text">{profile?.title}</span>
      </div>

      <p
        className="text-gray-400 max-w-lg mb-8 animate-slide-up text-base sm:text-lg"
        style={{ animationDelay: "0.4s" }}
      >
        {profile?.bio}
      </p>

      <div
        className="flex w-full flex-nowrap gap-2 animate-slide-up sm:w-auto sm:gap-4"
        style={{ animationDelay: "0.5s" }}
      >
        <Button
          variant="outline"
          asChild
          className="group h-9 min-w-0 flex-[1.25] border-teal/30 bg-transparent px-2 text-xs text-white hover:border-teal/80 hover:bg-teal/10 sm:h-10 sm:flex-none sm:px-4 sm:text-sm"
        >
          <a href="#blogs" aria-label="Scroll to blogs and projects section">
            <span
              className="typewriter whitespace-nowrap"
              style={{ "--typewriter-width": "18ch" } as React.CSSProperties}
            >
              View Blogs &amp; Projects
            </span>
            <ArrowRight className="ml-1.5 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1 sm:ml-2" />
          </a>
        </Button>

        <Button
          variant="outline"
          asChild
          className="group h-9 min-w-0 flex-[0.75] border-gray-700 bg-transparent px-2 text-xs text-white hover:border-gray-600 hover:bg-gray-800/50 sm:h-10 sm:flex-none sm:px-4 sm:text-sm"
        >
          <a
            href={profile?.resume_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="flex items-center justify-center gap-1.5 whitespace-nowrap">
              <span className="sm:hidden">Resume</span>
              <span className="hidden sm:inline">Download Resume</span>
              <ArrowDown className="h-4 w-4 shrink-0 animate-bob" />
            </span>
          </a>
        </Button>
      </div>
    </div>
  );
};

export default Hero;
